// services/baseballSeriesService.js
// Service for MLB season series visualization with opponent momentum calculations

class BaseballSeriesService {
  constructor() {
    this.cache = new Map();
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
  }

  // Series segmentation logic
  segmentGamesIntoSeries(games, teamId) {
    if (!games || games.length === 0) return [];
    
    console.log('Segmenting games into series:', { 
      teamId, 
      gamesCount: games.length 
    });
    
    // Sort games by date
    const sortedGames = [...games].sort((a, b) => 
      new Date(a.fixture?.date || a.date) - new Date(b.fixture?.date || b.date)
    );

    const series = [];
    let currentSeries = null;

    sortedGames.forEach((game) => {
      const gameDate = new Date(game.fixture?.date || game.date);
      const homeTeam = game.teams?.home || game.home;
      const awayTeam = game.teams?.away || game.away;
      
      // Skip non-final games
      if (game.fixture?.status?.short !== 'FT' && game.status?.short !== 'FT') {
        return;
      }

      // Determine opponent and venue from team perspective
      const isHome = homeTeam.id === teamId;
      const opponent = isHome ? awayTeam : homeTeam;
      const venuePov = isHome ? 'HOME' : 'AWAY';
      
      // Check if this starts a new series
      const shouldStartNewSeries = !currentSeries ||
        currentSeries.opponentId !== opponent.id ||
        currentSeries.venuePov !== venuePov ||
        this.daysBetween(currentSeries.lastGameDate, gameDate) > 1;

      if (shouldStartNewSeries) {
        // Save previous series if exists
        if (currentSeries) {
          series.push(this.finalizeSeries(currentSeries));
        }

        // Start new series
        currentSeries = {
          seriesIdx: series.length,
          startDate: gameDate,
          endDate: gameDate,
          lastGameDate: gameDate,
          opponentId: opponent.id,
          opponent: opponent,
          venuePov: venuePov,
          games: [],
          wins: 0,
          losses: 0,
          runDiff: 0
        };
      }

      // Add game to current series
      const homeScore = game.scores?.home?.total ?? game.score?.home ?? 0;
      const awayScore = game.scores?.away?.total ?? game.score?.away ?? 0;
      const teamScore = isHome ? homeScore : awayScore;
      const oppScore = isHome ? awayScore : homeScore;
      const won = teamScore > oppScore;

      currentSeries.games.push({
        gameId: game.fixture?.id || game.id,
        date: gameDate,
        won: won,
        teamScore: teamScore,
        oppScore: oppScore,
        runDiff: teamScore - oppScore
      });

      currentSeries.endDate = gameDate;
      currentSeries.lastGameDate = gameDate;
      currentSeries.wins += won ? 1 : 0;
      currentSeries.losses += won ? 0 : 1;
      currentSeries.runDiff += (teamScore - oppScore);
    });

    // Add the last series
    if (currentSeries) {
      series.push(this.finalizeSeries(currentSeries));
    }

    return series;
  }

  finalizeSeries(series) {
    return {
      ...series,
      netWins: series.wins - series.losses,
      gamesCount: series.games.length,
      midpointDate: new Date(
        (series.startDate.getTime() + series.endDate.getTime()) / 2
      ),
      durationDays: this.daysBetween(series.startDate, series.endDate) + 1
    };
  }

  daysBetween(date1, date2) {
    const oneDay = 24 * 60 * 60 * 1000;
    return Math.floor(Math.abs((date2 - date1) / oneDay));
  }

  // Calculate weighted L10 momentum
  calculateOpponentMomentum(opponentData, beforeDate) {
    const alpha = 0.85; // decay factor
    const beforeTime = new Date(beforeDate).getTime();
    
    // Extract games array from opponentData
    const opponentGames = opponentData?.games || opponentData || [];
    
    // Ensure we have an array
    if (!Array.isArray(opponentGames)) {
      console.warn('Opponent games is not an array:', opponentGames);
      return 0;
    }
    
    // Get last 10 games before the series start
    const last10 = opponentGames
      .filter(g => {
        const gameDate = new Date(g.fixture?.date || g.date).getTime();
        return gameDate < beforeTime && 
               (g.fixture?.status?.short === 'FT' || g.status?.short === 'FT');
      })
      .sort((a, b) => 
        new Date(b.fixture?.date || b.date) - new Date(a.fixture?.date || a.date)
      )
      .slice(0, 10);

    if (last10.length === 0) return 0;

    let weightedSum = 0;
    let weightSum = 0;

    last10.forEach((game, idx) => {
      const weight = Math.pow(alpha, idx);
      const won = this.didTeamWin(game, opponentData.teamId || opponentData?.teamId);
      const score = won ? 1 : -1;
      
      weightedSum += weight * score;
      weightSum += weight;
    });

    return weightSum > 0 ? weightedSum / weightSum : 0;
  }

  didTeamWin(game, teamId) {
    const homeTeam = game.teams?.home || game.home;
    const awayTeam = game.teams?.away || game.away;
    const homeScore = game.scores?.home?.total ?? game.score?.home ?? 0;
    const awayScore = game.scores?.away?.total ?? game.score?.away ?? 0;
    
    if (homeTeam.id === teamId) {
      return homeScore > awayScore;
    } else {
      return awayScore > homeScore;
    }
  }

  // Get momentum emoji based on score
  getMomentumEmoji(score) {
    if (score >= 0.35) return '🔥';
    if (score <= -0.35) return '❄️';
    return '';
  }

  // Process standings data to extract rankings
  processStandings(standings, teamId, date) {
    if (!standings || !standings.length) return null;

    // Find team in standings
    const teamStanding = standings.find(s => s.team?.id === teamId);
    if (!teamStanding) return null;

    return {
      leagueRank: teamStanding.position || teamStanding.rank || 0,
      divisionRank: teamStanding.divisionRank || 0,
      wins: teamStanding.all?.win || teamStanding.wins || 0,
      losses: teamStanding.all?.lose || teamStanding.losses || 0,
      gamesOver500: (teamStanding.all?.win || 0) - (teamStanding.all?.lose || 0),
      streak: teamStanding.streak || teamStanding.form || '',
      date: date
    };
  }

  // Build cumulative season timeline
  buildSeasonTimeline(games, teamId) {
    if (!games || games.length === 0) return [];

    const timeline = [];
    let cumulativeWins = 0;
    let cumulativeLosses = 0;

    const sortedGames = [...games]
      .filter(g => g.fixture?.status?.short === 'FT' || g.status?.short === 'FT')
      .sort((a, b) => 
        new Date(a.fixture?.date || a.date) - new Date(b.fixture?.date || b.date)
      );

    sortedGames.forEach((game) => {
      const won = this.didTeamWin(game, teamId);
      cumulativeWins += won ? 1 : 0;
      cumulativeLosses += won ? 0 : 1;

      timeline.push({
        date: new Date(game.fixture?.date || game.date),
        gameId: game.fixture?.id || game.id,
        wins: cumulativeWins,
        losses: cumulativeLosses,
        netRecord: cumulativeWins - cumulativeLosses,
        winPct: cumulativeWins / (cumulativeWins + cumulativeLosses)
      });
    });

    return timeline;
  }

  // Enrich series with opponent data
  async enrichSeriesWithOpponentData(series, opponentDataMap, standingsMap) {
    return series.map(s => {
      const opponentGames = opponentDataMap.get(s.opponentId);
      const momentum = opponentGames 
        ? this.calculateOpponentMomentum(opponentGames, s.startDate)
        : 0;

      // Get opponent rank at series midpoint
      const midpointKey = s.midpointDate.toISOString().split('T')[0];
      const opponentStanding = standingsMap.get(`${s.opponentId}_${midpointKey}`);

      return {
        ...s,
        opponentMomentum: momentum,
        momentumEmoji: this.getMomentumEmoji(momentum),
        opponentRank: opponentStanding?.leagueRank || null,
        opponentDivisionRank: opponentStanding?.divisionRank || null,
        opponentRecord: opponentStanding ? 
          `${opponentStanding.wins}-${opponentStanding.losses}` : null
      };
    });
  }

  // Cache management
  getCacheKey(teamId, season) {
    return `series_${teamId}_${season}`;
  }

  getCachedData(teamId, season) {
    const key = this.getCacheKey(teamId, season);
    const cached = this.cache.get(key);
    
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }
    
    this.cache.delete(key);
    return null;
  }

  setCachedData(teamId, season, data) {
    const key = this.getCacheKey(teamId, season);
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  clearCache() {
    this.cache.clear();
  }

  // Main processing function
  async processTeamSeason(teamId, season, games, standings, allTeamsGames = null) {
    // Check cache first
    const cached = this.getCachedData(teamId, season);
    if (cached) return cached;

    console.log('Processing team season:', { 
      teamId, 
      season, 
      totalGames: games.length,
      firstGame: games[0],
      gameStructure: games[0] ? Object.keys(games[0]) : []
    });

    // Segment games into series
    const teamGames = games.filter(g => {
      const homeId = g.teams?.home?.id || g.home?.id;
      const awayId = g.teams?.away?.id || g.away?.id;
      const isTeamGame = homeId === teamId || awayId === teamId;
      
      if (isTeamGame) {
        console.log('Found team game:', { 
          homeId, 
          awayId, 
          teamId,
          date: g.fixture?.date || g.date 
        });
      }
      
      return isTeamGame;
    });

    const series = this.segmentGamesIntoSeries(teamGames, teamId);
    
    // Build season timeline
    const timeline = this.buildSeasonTimeline(teamGames, teamId);

    // Process standings data if available
    const standingsMap = new Map();
    if (standings) {
      // This would need to be expanded with daily standings snapshots
      // For now, use current standings as approximation
      standings.forEach(standing => {
        const key = `${standing.team?.id}_current`;
        standingsMap.set(key, this.processStandings([standing], standing.team?.id));
      });
    }

    // Enrich with opponent data if available
    let enrichedSeries = series;
    if (allTeamsGames) {
      const opponentDataMap = new Map();
      // Group games by team
      allTeamsGames.forEach(game => {
        const homeId = game.teams?.home?.id || game.home?.id;
        const awayId = game.teams?.away?.id || game.away?.id;
        
        [homeId, awayId].forEach(id => {
          if (!opponentDataMap.has(id)) {
            opponentDataMap.set(id, { teamId: id, games: [] });
          }
          opponentDataMap.get(id).games.push(game);
        });
      });

      enrichedSeries = await this.enrichSeriesWithOpponentData(
        series, 
        opponentDataMap, 
        standingsMap
      );
    }

    const result = {
      teamId,
      season,
      series: enrichedSeries,
      timeline,
      summary: {
        totalGames: teamGames.length,
        totalSeries: series.length,
        totalWins: series.reduce((sum, s) => sum + s.wins, 0),
        totalLosses: series.reduce((sum, s) => sum + s.losses, 0),
        totalRunDiff: series.reduce((sum, s) => sum + s.runDiff, 0),
        currentStreak: this.calculateCurrentStreak(teamGames, teamId)
      }
    };

    // Cache the result
    this.setCachedData(teamId, season, result);
    
    return result;
  }

  calculateCurrentStreak(games, teamId) {
    const sortedGames = [...games]
      .filter(g => g.fixture?.status?.short === 'FT' || g.status?.short === 'FT')
      .sort((a, b) => 
        new Date(b.fixture?.date || b.date) - new Date(a.fixture?.date || a.date)
      );

    if (sortedGames.length === 0) return 'N/A';

    let streak = 0;
    let streakType = null;

    for (const game of sortedGames) {
      const won = this.didTeamWin(game, teamId);
      const currentType = won ? 'W' : 'L';

      if (streakType === null) {
        streakType = currentType;
        streak = 1;
      } else if (streakType === currentType) {
        streak++;
      } else {
        break;
      }
    }

    return `${streakType}${streak}`;
  }
}

export default new BaseballSeriesService();